import type { HubClient } from "@roqeet/core";
import { ApiHubClient } from "./api-hub.js";
import { SupabaseHubClient } from "./supabase-hub.js";
import { DisabledHubClient } from "./hub-client.js";

/**
 * RoQeet Hub 싱글턴(결정 #23/#31). 앱 전역에서 공유하며, UI/액션은 core의
 * {@link HubClient} 인터페이스로만 다룬다(결정 #10). 미구성(키 없음)이면
 * `isConfigured()`가 false를 반환해 UI가 진입점을 숨긴다 — Google 동기화(결정 #8)가
 * graceful하게 꺼지는 것과 동일한 패턴.
 */
interface HubBackendEnv {
  VITE_HUB_BACKEND?: string;
  VITE_ROQEET_HUB_API_URL?: string;
  VITE_SUPABASE_URL?: string;
  VITE_SUPABASE_ANON_KEY?: string;
}

function hasApiConfig(env: HubBackendEnv): boolean {
  return env.VITE_HUB_BACKEND === "api" && Boolean(env.VITE_ROQEET_HUB_API_URL);
}

function hasSupabaseConfig(env: HubBackendEnv): boolean {
  return Boolean(env.VITE_SUPABASE_URL && env.VITE_SUPABASE_ANON_KEY);
}

export function createHubClient(env: HubBackendEnv = import.meta.env): HubClient {
  if (hasApiConfig(env)) {
    return new ApiHubClient(apiBaseUrl(env)!);
  }
  if (hasSupabaseConfig(env)) {
    return new SupabaseHubClient(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY);
  }
  return new DisabledHubClient();
}

function apiBaseUrl(env: HubBackendEnv): string | undefined {
  return env.VITE_ROQEET_HUB_API_URL;
}

export const hubClient = createHubClient();

export { DisabledHubClient, RepositoryHubClient } from "./hub-client.js";
export type {
  AuthRepository,
  HubRepositorySet,
  CreateDownloadLeaseInput,
  CreatePendingFileInput,
  CreateUploadLeaseInput,
  DownloadLease,
  FileKind,
  FileLifecycle,
  FileMetadata,
  FileMetadataRepository,
  FileVisibility,
  FeedbackRepository,
  FinalizeUploadInput,
  LinkFileToProjectInput,
  MarkFileDeletedInput,
  MarkFileUploadedInput,
  ModerationRepository,
  ObjectStorageLeasePort,
  ObjectStoragePort,
  ProfileRepository,
  ProjectRepository,
  SetFileVisibilityInput,
  StatsRepository,
  StorageKindLimit,
  StorageLimitPolicy,
  UploadLease,
} from "./ports.js";
export { ApiHubClient } from "./api-hub.js";
export { SupabaseHubClient } from "./supabase-hub.js";
